import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBattle } from '../context/BattleProvider';
import { useUser } from '../hooks/useUser';
import { HostView } from './HostView';
import { ParticipantView } from './ParticipantView';
import { ViewerView } from './ViewerView';

// Entry point for /battle and /battle/:sessionId
// Determines whether to show the host creation form or route to the correct role view.

export const BattlePage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const { user } = useUser();
  const {
    session,
    role,
    isConnecting,
    error,
    hostStartSession,
    viewerJoin,
    participantRequestJoin,
  } = useBattle();

  const [title, setTitle] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  // If arriving via invite link, auto-join as viewer
  useEffect(() => {
    if (sessionId && !session && !isConnecting) {
      viewerJoin(sessionId);
    }
  }, [sessionId]);

  // ── Already in a session — route to the right view ────────────────────────
  if (session && role === 'host') return <HostView />;
  if (session && role === 'participant') return <ParticipantView />;
  if (session && role === 'viewer') {
    // Viewer can request to join as participant
    return (
      <div style={{ position: 'relative' }}>
        <ViewerView />
        {session.status !== 'ended' && user && (
          <div
            style={{
              position: 'fixed',
              bottom: 80,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-xl)',
              padding: '10px 20px',
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              zIndex: 50,
              boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
            }}
          >
            {requestSent ? (
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Request sent — waiting for host approval…
              </span>
            ) : (
              <>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Want to battle?
                </span>
                <button
                  className="btn-primary"
                  style={{ fontSize: 13, padding: '8px 18px' }}
                  onClick={async () => {
                    await participantRequestJoin(session.id);
                    setRequestSent(true);
                  }}
                >
                  Request to join
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Loading states ─────────────────────────────────────────────────────────
  if (isConnecting) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 60px)',
          color: 'var(--text-muted)',
          fontSize: 14,
        }}
      >
        Connecting to session…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 60px)',
          gap: 12,
        }}
      >
        <p style={{ color: '#f87171', fontSize: 14 }}>{error}</p>
        <button className="btn-ghost" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  // ── No session yet — host creation form ───────────────────────────────────
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - 60px)',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          padding: 32,
          width: '100%',
          maxWidth: 420,
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.6rem',
            fontWeight: 800,
            marginBottom: 6,
          }}
        >
          Start a Battle
        </h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24 }}>
          Create a rap battle or battle of the bands session. Share the invite link to bring in
          participants and viewers.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label>
            <span
              style={{
                fontSize: 13,
                color: 'var(--text-secondary)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Session title
            </span>
            <input
              type="text"
              placeholder="e.g. Friday Night Rap Battle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && title.trim() && hostStartSession(title.trim())}
            />
          </label>

          <button
            className="btn-primary"
            style={{ padding: '12px', fontSize: 15 }}
            disabled={!title.trim() || !user}
            onClick={() => hostStartSession(title.trim())}
          >
            Create session
          </button>

          {!user && (
            <p style={{ fontSize: 12, color: '#f87171', textAlign: 'center' }}>
              You need to be logged in to host a session.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
